import { compressImage } from "@/utils/imageCompression";
import {
  BrowserImagePreparationError,
  isSupportedBrowserImage,
} from "./browserImageUploadPolicy";

export {
  BROWSER_IMAGE_ACCEPT,
  BROWSER_IMAGE_FORMAT_LABEL,
  BrowserImagePreparationError,
  isSupportedBrowserImage,
} from "./browserImageUploadPolicy";

export interface BrowserImagePreparationOptions {
  maxInputBytes: number;
  maxOutputBytes: number;
  maxWidth: number;
  maxHeight: number;
  quality: number;
  targetFileSizeMB: number;
}

/**
 * Shared browser-side upload preparation. It decodes the selected image,
 * applies browser orientation, bounds its dimensions, and emits a metadata-free
 * JPEG. Callers choose limits appropriate to their destination.
 */
export async function prepareBrowserImageUpload(
  file: File,
  options: BrowserImagePreparationOptions
): Promise<File> {
  if (!isSupportedBrowserImage(file)) {
    throw new BrowserImagePreparationError("unsupported_format");
  }
  if (file.size > options.maxInputBytes) {
    throw new BrowserImagePreparationError("input_too_large");
  }

  let preparedFile: File;
  try {
    preparedFile = await compressImage(file, {
      maxWidth: options.maxWidth,
      maxHeight: options.maxHeight,
      quality: options.quality,
      maxFileSizeMB: options.targetFileSizeMB,
      outputMimeType: "image/jpeg",
    });
  } catch {
    throw new BrowserImagePreparationError("unreadable_image");
  }

  if (preparedFile.size <= 0) {
    throw new BrowserImagePreparationError("unreadable_image");
  }
  if (preparedFile.size > options.maxOutputBytes) {
    throw new BrowserImagePreparationError("output_too_large");
  }
  return preparedFile;
}
